// Inline artifact preview component.
// Copied + factored from frontend/src/components/agent/InlineArtifactPreview.tsx.
// Factoring changes:
//   - Removed PublishWebappDialog (Platinum-specific); replaced with optional onPublishWebapp prop.
//   - Removed useAccount (AccountContext); customer passed as optional prop.
//   - Removed @/ imports; API base URL passed as prop.
//   - Removed isImageArtifact/getArtifactImageUrl utils; inlined generically.
//   - Removed @untitledui/file-icons; replaced with emoji fallback.
// See ../../docs/90-provenance-map.md.

import React, { useState, useRef, useCallback, useEffect, useContext } from 'react'
import { Box, Typography, IconButton, Tooltip } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import ShareIcon from '@mui/icons-material/Share'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import type { ArtifactInfo } from '../types.js'
import type { DeckAsk, PresentationAskState } from '../presentationFrame.js'
import usePresentationFrame from '../hooks/usePresentationFrame.js'
import { webappFrameUrl } from '../artifactFilters.js'
import { triggerArtifactDownload } from '../downloadArtifact.js'
import { useArtifactText } from '../hooks/useArtifactText.js'
import AgentMarkdown from './AgentMarkdown.js'
import { CARD_RADIUS, HAIRLINE, HAIRLINE_HOVER_COLOR, INNER_RADIUS } from './surface.js'
import { WebappFramesPausedContext } from './webappFrames.js'

/** The wait while a web app copies to cloud storage. The server only knows
 *  "there yet or not", so a seconds count is the honest progress we can show:
 *  it proves the wait is moving rather than stuck. */
function UploadingPlaceholder() {
  const [seconds, setSeconds] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setSeconds(s => s + 1), 1000)
    return () => clearInterval(t)
  }, [])
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 1.5, backgroundColor: '#f8fafc' }}>
      <Box sx={{ width: 28, height: 28, border: '3px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
      <Typography sx={{ color: '#6b7280', fontSize: 13 }}>Saving your web app to cloud storage…</Typography>
      <Typography sx={{ color: '#9ca3af', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
        {seconds}s{seconds >= 5 ? ' · usually under a minute' : ''}
      </Typography>
    </Box>
  )
}

interface InlineArtifactPreviewProps {
  artifact: ArtifactInfo
  sessionId: string
  onOpenPreview: (artifact: ArtifactInfo) => void
  /** When provided, skip HTTP fetch and use this data URL directly. */
  dataUrl?: string
  /** API base URL. Default: empty string (same origin). */
  apiBaseUrl?: string
  /** Auth header value. */
  authHeader?: string
  /** Optional callback for publish/share action on webapp artifacts. */
  onPublishWebapp?: (sessionId: string, artifact: ArtifactInfo) => void
  /** Edit a story board in place (#1416). The host passes it only to people who may edit. */
  onEditStory?: (sessionId: string, artifact: ArtifactInfo) => void
  /**
   * A presentation's Ask was pressed (a web app whose page is a deck). The
   * host opens its dialog; nothing is sent. Omitted: the page draws no Ask.
   */
  onAskFromPage?: (sessionId: string, artifact: ArtifactInfo, ask: DeckAsk) => void
  /** Why Ask is closed here, drawn by the page beside a disabled Ask. Undefined: Ask works. */
  askFromPageRefusal?: string
}

const IMAGE_EXTENSIONS = new Set(['.png', '.jpg', '.jpeg', '.svg', '.gif', '.webp'])

function isImageArtifact(artifact: ArtifactInfo): boolean {
  if (artifact.artifactType === 'image' || artifact.artifactType === 'chart') return true
  if (artifact.artifactType === 'file') {
    const ext = '.' + artifact.fileName.split('.').pop()?.toLowerCase()
    return IMAGE_EXTENSIONS.has(ext)
  }
  return false
}

const VIDEO_EXTENSIONS = new Set(['.mp4', '.webm', '.mov'])

/** The agent's narrated MP4 (Adam, 14 Sep): a file artifact told apart by its name. */
function isVideoArtifact(artifact: ArtifactInfo): boolean {
  if (artifact.artifactType !== 'file') return false
  const ext = '.' + artifact.fileName.split('.').pop()?.toLowerCase()
  return VIDEO_EXTENSIONS.has(ext)
}

function getArtifactImageUrl(artifact: ArtifactInfo, sessionId: string, baseUrl: string): string | null {
  if (artifact.status === 'lost') return null
  if (artifact.id) return `${baseUrl}/agent/artifacts/${artifact.id}/download`
  const filePath = artifact.filePath.replace(/^\//, '')
  return `${baseUrl}/agent/session/${sessionId}/workspace/files/${filePath}`
}

export default function InlineArtifactPreview({
  artifact,
  sessionId,
  onOpenPreview,
  dataUrl,
  apiBaseUrl = '',
  authHeader,
  onPublishWebapp,
  onEditStory,
  onAskFromPage,
  askFromPageRefusal,
}: InlineArtifactPreviewProps) {
  const webappFramesPaused = useContext(WebappFramesPausedContext)
  // The card's text: shared with the chat's other cards in one request (#2022).
  const content = useArtifactText({
    artifact,
    sessionId,
    apiBaseUrl,
    authHeader,
    enabled: (artifact.artifactType === 'code' || artifact.artifactType === 'csv' || artifact.artifactType === 'report')
      && artifact.status !== 'lost',
  })

  const handleDownload = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    triggerArtifactDownload({ artifact, sessionId, apiBaseUrl, authHeader })
      .catch(err => console.error('Download failed:', err))
  }, [artifact, sessionId, apiBaseUrl, authHeader])

  const handleClick = useCallback(() => {
    onOpenPreview(artifact)
  }, [artifact, onOpenPreview])

  if (artifact.status === 'lost') {
    return (
      <Box sx={{ border: HAIRLINE, borderRadius: CARD_RADIUS, p: 1.5, mt: 1, backgroundColor: '#f9fafb' }}>
        <Typography sx={{ fontSize: 13, color: '#6b7280' }}>
          {artifact.label} — no longer available
        </Typography>
      </Box>
    )
  }

  if (isVideoArtifact(artifact)) {
    return (
      <VideoPreview
        artifact={artifact}
        sessionId={sessionId}
        onOpenPreview={handleClick}
        onDownload={handleDownload}
        onPublish={onPublishWebapp ? () => onPublishWebapp(sessionId, artifact) : undefined}
        apiBaseUrl={apiBaseUrl}
        authHeader={authHeader}
      />
    )
  }

  if (isImageArtifact(artifact)) {
    return (
      <ImagePreview
        artifact={artifact}
        sessionId={sessionId}
        onClick={handleClick}
        onDownload={handleDownload}
        dataUrl={dataUrl}
        apiBaseUrl={apiBaseUrl}
        authHeader={authHeader}
      />
    )
  }

  // Code and CSV artifacts are the same 12px card as every other artifact;
  // the glimpse of the file sits one level inside it at the inner corner, and
  // the caption bar is clipped by the card like the image and report captions.
  if (artifact.artifactType === 'code') {
    return (
      <Box onClick={handleClick} sx={{ mt: 1, border: HAIRLINE, borderRadius: CARD_RADIUS, overflow: 'hidden', backgroundColor: '#f9fafb', cursor: 'pointer', '&:hover': { borderColor: HAIRLINE_HOVER_COLOR } }}>
        <Box sx={{ p: 1 }}>
          {content?.text ? (
            <CodePreview code={content.text} fileName={artifact.fileName} />
          ) : content?.error ? (
            <ErrorCard message={content.error} />
          ) : (
            <LoadingCard />
          )}
        </Box>
        <CaptionBar artifact={artifact} onDownload={handleDownload} />
      </Box>
    )
  }

  if (artifact.artifactType === 'csv') {
    return (
      <Box onClick={handleClick} sx={{ mt: 1, border: HAIRLINE, borderRadius: CARD_RADIUS, overflow: 'hidden', backgroundColor: '#f9fafb', cursor: 'pointer', '&:hover': { borderColor: HAIRLINE_HOVER_COLOR } }}>
        <Box sx={{ p: 1 }}>
          {content?.text ? (
            <CsvPreview csv={content.text} />
          ) : content?.error ? (
            <ErrorCard message={content.error} />
          ) : (
            <LoadingCard />
          )}
        </Box>
        <CaptionBar artifact={artifact} onDownload={handleDownload} />
      </Box>
    )
  }

  if (artifact.artifactType === 'webapp') {
    const isReady = artifact.status === 'extracted'
    const framesPaused = webappFramesPaused
    const iframeSrc = webappFrameUrl(artifact, sessionId, apiBaseUrl, authHeader)

    return (
      <Box sx={{
        mt: 1,
        border: '1.5px solid #3b82f6',
        borderRadius: CARD_RADIUS,
        overflow: 'hidden',
        boxShadow: '0 1px 6px rgba(59, 130, 246, 0.15)',
      }}>
        {isReady && framesPaused ? (
          <Box data-testid="webapp-frame-paused" sx={{ height: 800, backgroundColor: '#0f172a' }} />
        ) : isReady ? (
          <WebappIframe
            key={iframeSrc}
            src={iframeSrc}
            title={artifact.label || 'Interactive Visualization'}
            askState={onAskFromPage ? { enabled: !askFromPageRefusal, reason: askFromPageRefusal } : undefined}
            onAsk={onAskFromPage ? ask => onAskFromPage(sessionId, artifact, ask) : undefined}
          />
        ) : (
          <UploadingPlaceholder />
        )}
        <WebappCaptionBar
          artifact={artifact}
          onFullscreen={handleClick}
          onPublish={onPublishWebapp ? () => onPublishWebapp(sessionId, artifact) : undefined}
          onEdit={onEditStory && artifact.id && isReady ? () => onEditStory(sessionId, artifact) : undefined}
        />
      </Box>
    )
  }

  if (artifact.artifactType === 'report') {
    return (
      <Box onClick={handleClick} sx={{ mt: 1, border: HAIRLINE, borderRadius: CARD_RADIUS, overflow: 'hidden', cursor: 'pointer', '&:hover': { borderColor: HAIRLINE_HOVER_COLOR } }}>
        {content?.text ? (
          <Box sx={{ p: 1.5, maxHeight: 150, overflow: 'hidden' }}>
            <AgentMarkdown content={content.text.slice(0, 500)} />
          </Box>
        ) : content?.error ? (
          <ErrorCard message={content.error} />
        ) : (
          <LoadingCard />
        )}
        <CaptionBar artifact={artifact} onDownload={handleDownload} />
      </Box>
    )
  }

  // Generic file artifact
  return (
    <Box onClick={handleClick} sx={{ mt: 1, border: HAIRLINE, borderRadius: CARD_RADIUS, overflow: 'hidden', backgroundColor: '#f9fafb', cursor: 'pointer', '&:hover': { borderColor: HAIRLINE_HOVER_COLOR } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}>
        <Typography sx={{ fontSize: 20 }}>📄</Typography>
        <Typography sx={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{artifact.fileName}</Typography>
      </Box>
      <CaptionBar artifact={artifact} onDownload={handleDownload} />
    </Box>
  )
}

function ImagePreview({ artifact, sessionId, onClick, onDownload, dataUrl, apiBaseUrl = '', authHeader }: { artifact: ArtifactInfo; sessionId: string; onClick: () => void; onDownload: (e: React.MouseEvent) => void; dataUrl?: string; apiBaseUrl?: string; authHeader?: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(dataUrl || null)
  const fetchStartedRef = useRef(!!dataUrl)

  if (!fetchStartedRef.current) {
    fetchStartedRef.current = true
    const rawUrl = getArtifactImageUrl(artifact, sessionId, apiBaseUrl)
    if (rawUrl) {
      const h: Record<string, string> = {}
      if (authHeader) h['Authorization'] = authHeader
      fetch(rawUrl, { headers: h })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          return res.blob()
        })
        .then(blob => setBlobUrl(URL.createObjectURL(blob)))
        .catch(err => console.error('Image load failed:', err))
    }
  }

  return (
    <Box onClick={onClick} sx={{ display: 'inline-block', border: HAIRLINE, borderRadius: CARD_RADIUS, mt: 1, overflow: 'hidden', backgroundColor: '#f9fafb', cursor: 'pointer', maxWidth: '75%', '&:hover': { borderColor: HAIRLINE_HOVER_COLOR } }}>
      {blobUrl ? (
        <img src={blobUrl} alt={artifact.label} style={{ display: 'block', maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }} />
      ) : (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Loading image...</Typography>
        </Box>
      )}
      <CaptionBar artifact={artifact} onDownload={onDownload} />
    </Box>
  )
}

// A video plays in the card itself (Adam, 14 Sep: "you have to click it to
// play it"). Fetched as a blob like an image so the bearer token travels;
// the player's own controls take the click, the caption opens the dialog.
function VideoPreview({ artifact, sessionId, onOpenPreview, onDownload, onPublish, apiBaseUrl = '', authHeader }: { artifact: ArtifactInfo; sessionId: string; onOpenPreview: () => void; onDownload: (e: React.MouseEvent) => void; onPublish?: () => void; apiBaseUrl?: string; authHeader?: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fetchedKeyRef = useRef<string | null>(null)
  const fetchKey = `${artifact.id ?? ''}:${artifact.status}`
  if (fetchedKeyRef.current !== fetchKey) {
    fetchedKeyRef.current = fetchKey
    const rawUrl = getArtifactImageUrl(artifact, sessionId, apiBaseUrl)
    if (rawUrl) {
      const h: Record<string, string> = {}
      if (authHeader) h['Authorization'] = authHeader
      fetch(rawUrl, { headers: h })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          return res.blob()
        })
        .then(blob => { setError(null); setBlobUrl(URL.createObjectURL(blob)) })
        .catch(err => {
          // A just-registered file may not be written yet; the next update
          // (id or status) retries. Only a settled artifact shows the error.
          if (artifact.status === 'extracted') setError(err instanceof Error ? err.message : 'Failed to load')
        })
    }
  }
  return (
    <Box data-testid="artifact-video-card" sx={{ mt: 1, border: HAIRLINE, borderRadius: CARD_RADIUS, overflow: 'hidden', backgroundColor: '#000', maxWidth: 720, '&:hover': { borderColor: HAIRLINE_HOVER_COLOR } }}>
      {blobUrl ? (
        <video data-testid="artifact-video-inline" src={blobUrl} controls preload="metadata" style={{ display: 'block', width: '100%', maxHeight: 405, background: '#000' }} />
      ) : error ? (
        <ErrorCard message={error} />
      ) : (
        <Box sx={{ p: 2, textAlign: 'center', backgroundColor: '#f9fafb' }}>
          <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Loading video...</Typography>
        </Box>
      )}
      <Box onClick={onOpenPreview} sx={{ cursor: 'pointer' }}>
        <CaptionBar artifact={artifact} onDownload={onDownload} onPublish={onPublish} />
      </Box>
    </Box>
  )
}

function CaptionBar({ artifact, onDownload, onPublish }: { artifact: ArtifactInfo; onDownload: (e: React.MouseEvent) => void; onPublish?: () => void }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '6px 10px', borderTop: HAIRLINE, backgroundColor: '#f9fafb' }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {artifact.label}
        </Typography>
        {artifact.description && (
          <Typography sx={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {artifact.description}
          </Typography>
        )}
      </Box>
      {onPublish && (
        <Tooltip title="Share: publish to DataViz">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onPublish() }}
            aria-label="publish"
            sx={{ color: '#2563eb', ml: 0.5 }}
          >
            <ShareIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
      <IconButton size="small" onClick={onDownload} aria-label="download" sx={{ color: '#2563eb', ml: 0.5 }}>
        <DownloadIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  )
}

function WebappIframe({ src, title, askState, onAsk }: {
  src: string
  title: string
  askState?: PresentationAskState
  onAsk?: (ask: DeckAsk) => void
}) {
  const [loading, setLoading] = useState(true)
  const frameRef = useRef<HTMLIFrameElement>(null)
  usePresentationFrame({ frameRef, askState, onAsk })
  return (
    <Box sx={{ position: 'relative', backgroundColor: '#0f172a' }}>
      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
          <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Loading visualization...</Typography>
        </Box>
      )}
      <iframe
        ref={frameRef}
        src={src}
        sandbox="allow-scripts allow-same-origin"
        onLoad={() => setLoading(false)}
        style={{ width: '100%', height: 800, border: 'none', display: 'block', opacity: loading ? 0 : 1, transition: 'opacity 0.2s' }}
        title={title}
      />
    </Box>
  )
}

// No download here (#1578 item 16): a zipped web app cannot run from a folder
// on disk (fetch and ES modules are blocked on file://). Its dashboard offers
// Download PDF instead.
function WebappCaptionBar({ artifact, onFullscreen, onPublish, onEdit }: { artifact: ArtifactInfo; onFullscreen: () => void; onPublish?: () => void; onEdit?: () => void }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '6px 10px', borderTop: HAIRLINE, backgroundColor: '#f9fafb' }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {artifact.label}
        </Typography>
        {artifact.description && (
          <Typography sx={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {artifact.description}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        {onEdit && (
          <Tooltip title="Edit this story board">
            <IconButton size="small" onClick={onEdit} aria-label="Edit story board" data-testid="inline-edit-story" sx={{ color: '#2563eb' }}>
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        {onPublish && (
          <Tooltip title="Share: publish to DataViz">
            <IconButton size="small" onClick={onPublish} aria-label="publish" sx={{ color: '#2563eb' }}>
              <ShareIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" onClick={onFullscreen} aria-label="fullscreen" sx={{ color: '#2563eb' }}>
          <FullscreenIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  )
}

function CodePreview({ code, fileName }: { code: string; fileName: string }) {
  const lines = code.split('\n').slice(0, 5).join('\n')
  return (
    <Box sx={{ border: HAIRLINE, borderRadius: INNER_RADIUS, overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 0.75, backgroundColor: '#f1f5f9', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{fileName}</Typography>
      </Box>
      <Box component="pre" sx={{ m: 0, p: '8px 12px', fontSize: 11, color: '#374151', fontFamily: 'monospace', overflow: 'hidden', maxHeight: 90, whiteSpace: 'pre' }}>
        {lines}
      </Box>
    </Box>
  )
}

function CsvPreview({ csv }: { csv: string }) {
  const rows = csv.trim().split('\n').slice(0, 4)
  return (
    <Box sx={{ border: HAIRLINE, borderRadius: INNER_RADIUS, overflow: 'auto', maxHeight: 90 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.split(',').map((cell, j) => (
                <td key={j} style={{ padding: '2px 8px', border: '1px solid #e5e7eb', color: i === 0 ? '#374151' : '#6b7280', fontWeight: i === 0 ? 600 : 400 }}>
                  {cell.trim()}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  )
}

function LoadingCard() {
  return (
    <Box sx={{ p: 1.5, border: HAIRLINE, borderRadius: INNER_RADIUS, backgroundColor: '#f8fafc' }}>
      <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Loading preview...</Typography>
    </Box>
  )
}

function ErrorCard({ message }: { message: string }) {
  return (
    <Box sx={{ p: 1.5, border: HAIRLINE, borderRadius: INNER_RADIUS, backgroundColor: '#fef2f2' }}>
      <Typography sx={{ fontSize: 12, color: '#ef4444' }}>Failed to load: {message}</Typography>
    </Box>
  )
}
